import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import MobileBottomNav from "@/components/MobileBottomNav";
import DailyVisitBonus from "@/components/DailyVisitBonus";
import HomeCookieCacheBanner from "@/components/HomeCookieCacheBanner";
import RoutePageLoader from "@/components/RoutePageLoader";
import { getImageCacheConsentStatus, registerImageCacheServiceWorker } from "@/lib/imageCacheConsent";
import { PublicRoutes } from "@/routes/PublicRoutes";
import { UserRoutes } from "@/routes/UserRoutes";
import { AdminRoutes } from "@/routes/AdminRoutes";
import { LogisticsRoutes } from "@/routes/LogisticsRoutes";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
};

const AppContent = () => {
  useEffect(() => {
    if (getImageCacheConsentStatus() !== "accepted") return;
    void registerImageCacheServiceWorker();
  }, []);

  return (
    <div className="pb-24 md:pb-0">
      <ScrollToTop />
      <RoutePageLoader />
      <Routes>
        {PublicRoutes()}
        {UserRoutes()}
        {AdminRoutes()}
        {LogisticsRoutes()}
      </Routes>

      <HomeCookieCacheBanner />
      <DailyVisitBonus />
      <MobileBottomNav />
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AppContent />
      </TooltipProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
